import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, MessageCircle, Linkedin, Instagram } from 'lucide-react';
import { companyInfo } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-black text-white pt-16 pb-8 border-t border-white/10">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div>
            <img
              src="https://customer-assets.emergentagent.com/job_enterprise-partner/artifacts/zkonwfm4_WhatsApp_Image_2026-02-11_at_12.05.31_PM__2_-removebg-preview.png"
              alt="SUM-IT Corporate Services logo"
              className="h-64 w-auto mb-4"
              loading="lazy"
              decoding="async"
            />
            <p className="text-gray-400 text-sm leading-relaxed mb-4 max-w-xs">
              {companyInfo.tagline}
            </p>
            <div className="flex space-x-4">
              <a
                href="https://wa.me/971507163176"
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 rounded-full bg-[#25D366] flex items-center justify-center hover:bg-[#1FB855] transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                aria-label="Chat with us on WhatsApp"
                data-testid="footer-whatsapp-link"
              >
                <MessageCircle size={20} />
              </a>
              <a
                href="https://www.linkedin.com/company/sum-it-corporate-services-provider/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 rounded-full bg-[#0077B5] flex items-center justify-center hover:bg-[#006399] transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                aria-label="Visit our LinkedIn page"
                data-testid="footer-linkedin-link"
              >
                <Linkedin size={20} />
              </a>
              <a
                href="https://www.instagram.com/sumit_corporate?igsh=bDVuOGUzOGlkOWk2&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="w-11 h-11 rounded-full bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F56040] flex items-center justify-center hover:opacity-80 transition-opacity duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                aria-label="Visit our Instagram page"
                data-testid="footer-instagram-link"
              >
                <Instagram size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#B19CD9]">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/"
                  className="text-gray-400 hover:text-[#B19CD9] transition-colors duration-200 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/services"
                  className="text-gray-400 hover:text-[#B19CD9] transition-colors duration-200 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm"
                >
                  Services
                </Link>
              </li>
              <li>
                <Link
                  to="/newsroom"
                  className="text-gray-400 hover:text-[#B19CD9] transition-colors duration-200 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm"
                  data-testid="footer-newsroom-link"
                >
                  Newsroom
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="text-gray-400 hover:text-[#B19CD9] transition-colors duration-200 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-gray-400 hover:text-[#B19CD9] transition-colors duration-200 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm"
                >
                  Get a Quote
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-gray-400 hover:text-[#B19CD9] transition-colors duration-200 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm"
                >
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#B19CD9]">Our Services</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/company-setup-freezone-dubai-uae" className="text-gray-400 hover:text-[#B19CD9] transition-colors duration-200 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm" data-testid="footer-service-company-setup-link">
                  Company Formation
                </Link>
              </li>
              <li>
                <Link to="/accounting-bookkeeping-services-dubai-uae" className="text-gray-400 hover:text-[#B19CD9] transition-colors duration-200 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm" data-testid="footer-service-accounting-link">
                  Accounting &amp; Bookkeeping
                </Link>
              </li>
              <li>
                <Link to="/tax-services-dubai-uae" className="text-gray-400 hover:text-[#B19CD9] transition-colors duration-200 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm" data-testid="footer-service-tax-link">
                  Tax Services
                </Link>
              </li>
              <li>
                <Link to="/hr-services-dubai-uae" className="text-gray-400 hover:text-[#B19CD9] transition-colors duration-200 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm" data-testid="footer-service-hr-link">
                  HR Services
                </Link>
              </li>
              <li>
                <Link to="/management-consultancy-dubai-uae" className="text-gray-400 hover:text-[#B19CD9] transition-colors duration-200 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm" data-testid="footer-service-management-link">
                  Management Consultancy
                </Link>
              </li>
              <li>
                <Link to="/corporate-governance-compliance-dubai-uae" className="text-gray-400 hover:text-[#B19CD9] transition-colors duration-200 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm" data-testid="footer-service-governance-link">
                  Corporate Governance
                </Link>
              </li>
              <li>
                <Link to="/corporate-services-dubai-uae" className="text-gray-400 hover:text-[#B19CD9] transition-colors duration-200 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm" data-testid="footer-service-bank-account-link">
                  Bank Account Opening
                </Link>
              </li>
              <li>
                <Link to="/legal-assistance-dubai-uae" className="text-gray-400 hover:text-[#B19CD9] transition-colors duration-200 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm" data-testid="footer-service-legal-link">
                  Legal Assistance
                </Link>
              </li>
              <li>
                <Link to="/audit-assistance-dubai-uae" className="text-gray-400 hover:text-[#B19CD9] transition-colors duration-200 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm" data-testid="footer-service-audit-link">
                  Audit Assistance
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#B19CD9]">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Mail size={18} className="text-[#B19CD9] mt-1 flex-shrink-0" />
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="text-gray-400 hover:text-[#B19CD9] transition-colors text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm"
                >
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin size={18} className="text-[#B19CD9] mt-1 flex-shrink-0" />
                <span className="text-gray-400 text-sm">{companyInfo.address}</span>
              </li>
              <li className="flex items-start space-x-3">
                <MessageCircle size={18} className="text-[#B19CD9] mt-1 flex-shrink-0" />
                <a
                  href={`https://wa.me/${companyInfo.whatsapp.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#B19CD9] transition-colors text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm"
                >
                  {companyInfo.whatsapp}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © {currentYear} {companyInfo.name}. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link
                to="/privacy"
                className="text-gray-400 hover:text-[#B19CD9] transition-colors text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-gray-400 hover:text-[#B19CD9] transition-colors text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B19CD9] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
